import { useEffect, useState } from "react";
import AdminLayout from "./components/AdminLayout";
import {
  fetchCategoryNames,
  mergeCategoryOptions,
} from "@/lib/productHelpers";

type Product = {
  id: string;
  category?: string;
  categories?: string[];
  name: string;
  description?: string;
  images?: string[];
  main_image?: string;
  video_url?: string;
  visible?: boolean;
  featured?: boolean;
};

const API = "/api/products.php";

const CATEGORY_OPTIONS = [
  "Alimentício",
  "Metalúrgico",
  "Têxtil",
  "Automobilístico",
  "Higiene Pessoal",
  "Automação",
];

const getProductCategories = (product: Partial<Product>) => {
  const categories = product.categories?.filter(Boolean) ?? [];

  if (categories.length > 0) {
    return categories;
  }

  return product.category ? [product.category] : [];
};

const formatProductCategories = (product: Partial<Product>) =>
  getProductCategories(product).join(", ");

export default function ProdutosAdminPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Partial<Product> | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [categoryOptions, setCategoryOptions] = useState<string[]>(CATEGORY_OPTIONS);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const [response, savedCategories] = await Promise.all([
        fetch(API),
        fetchCategoryNames(),
      ]);
      const data = await response.json();
      setProducts(data);
      setCategoryOptions(
        mergeCategoryOptions([
          savedCategories,
          CATEGORY_OPTIONS,
          data.flatMap((product: Product) => getProductCategories(product)),
        ])
      );
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const filtered = products.filter((p) =>
    p.name?.toLowerCase().includes(search.toLowerCase())
  );

  const openNew = () => {
    const defaultCategory = categoryOptions[0] ?? CATEGORY_OPTIONS[0];

    setEditing({
      category: "Automação",
      categories: ["Automação"],
      name: "",
      description: "",
      images: [""],
      main_image: "",
      video_url: "",
      visible: true,
      featured: false,
    });
    setEditing((product) =>
      product
        ? { ...product, category: defaultCategory, categories: [defaultCategory] }
        : product
    );
    setShowModal(true);
  };

  const openEdit = (p: Product) => {
    setEditing({
      ...p,
      categories: getProductCategories(p),
      images: p.images && p.images.length > 0 ? p.images : [""],
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
  };

  const handleSave = async () => {
    if (!editing?.name) {
      alert("Digite o nome");
      return;
    }

    const categories = getProductCategories(editing);

    if (categories.length === 0) {
      alert("Selecione ao menos uma categoria");
      return;
    }

    try {
      const payload = {
        ...editing,
        category: categories[0],
        categories,
        images: editing.images?.filter((img) => img.trim() !== "") ?? [],
        main_image: editing.main_image ?? editing.images?.[0] ?? "",
        video_url: editing.video_url?.trim() ?? "",
      };

      if (editing.id) {
        await fetch(`${API}?id=${encodeURIComponent(editing.id)}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      } else {
        await fetch(API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      }

      closeModal();
      fetchProducts();
    } catch (err) {
      console.error(err);
      alert("Erro ao salvar");
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await fetch(`${API}?id=${encodeURIComponent(id)}`, { method: "DELETE" });
      setDeleteId(null);
      fetchProducts();
    } catch (err) {
      console.error(err);
    }
  };

  const toggleVisible = async (p: Product) => {
    try {
      await fetch(`${API}?id=${encodeURIComponent(p.id)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ visible: !p.visible }),
      });
      fetchProducts();
    } catch (err) {
      console.error(err);
    }
  };

  const updateImage = (index: number, value: string) => {
    if (!editing) return;
    const updated = [...(editing.images ?? [])];
    updated[index] = value;
    setEditing({ ...editing, images: updated });
  };

  const addImageField = () => {
    if (!editing) return;
    setEditing({ ...editing, images: [...(editing.images ?? []), ""] });
  };

  const removeImageField = (index: number) => {
    if (!editing) return;
    const updated = (editing.images ?? []).filter((_, i) => i !== index);
    setEditing({ ...editing, images: updated });
  };

  const toggleCategory = (category: string) => {
    if (!editing) return;

    const categories = getProductCategories(editing);
    const updated = categories.includes(category)
      ? categories.filter((item) => item !== category)
      : [...categories, category];

    setEditing({
      ...editing,
      category: updated[0] ?? "",
      categories: updated,
    });
  };

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Produtos</h1>
        <button
          onClick={openNew}
          className="bg-[#1B1F5E] text-white px-4 py-2 rounded-lg text-sm sm:text-base"
        >
          Novo Produto
        </button>
      </div>

      {/* Search */}
      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Buscar..."
        className="w-full border p-3 rounded-lg mb-5"
      />

      {/* Product List */}
      {loading ? (
        <p>Carregando...</p>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filtered.map((p) => (
            <div
              key={p.id}
              className="border rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
            >
              <div className="flex items-center gap-4">
                <img
                  src={p.main_image || p.images?.[0] || "https://placehold.co/300x300"}
                  className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                  alt={p.name}
                />
                <div className="min-w-0">
                  <p className="text-xs text-gray-400 mb-1">
                    {formatProductCategories(p)}
                  </p>
                  <h2 className="font-bold truncate">{p.name}</h2>
                  <p className="text-sm text-gray-500 line-clamp-2">{p.description}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 sm:flex-nowrap sm:flex-shrink-0">
                <button
                  onClick={() => toggleVisible(p)}
                  className="border px-3 py-2 rounded-lg text-sm whitespace-nowrap"
                >
                  {p.visible ? "Ocultar" : "Mostrar"}
                </button>
                <button
                  onClick={() => openEdit(p)}
                  className="border px-3 py-2 rounded-lg text-sm"
                >
                  Editar
                </button>
                <button
                  onClick={() => setDeleteId(p.id)}
                  className="bg-red-500 text-white px-3 py-2 rounded-lg text-sm"
                >
                  Excluir
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit/Create Modal */}
      {showModal && editing && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl w-full max-w-2xl my-4 sm:my-8 shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-lg font-bold text-gray-800">
                {editing.id ? "Editar Produto" : "Novo Produto"}
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                aria-label="Fechar"
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 space-y-4 overflow-y-auto max-h-[70vh]">
              {/* Categories */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Categorias
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {categoryOptions.map((category) => (
                    <label
                      key={category}
                      className="flex items-center gap-2 border rounded-lg p-3 cursor-pointer hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={getProductCategories(editing).includes(category)}
                        onChange={() => toggleCategory(category)}
                        className="w-4 h-4 accent-[#1B1F5E]"
                      />
                      <span className="text-sm text-gray-700">{category}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome <span className="text-red-500">*</span>
                </label>
                <input
                  value={editing.name ?? ""}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  placeholder="Nome do produto"
                  className="w-full border p-3 rounded-lg"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <textarea
                  value={editing.description ?? ""}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  placeholder="Descrição do produto"
                  className="w-full border p-3 rounded-lg min-h-[100px] resize-y"
                />
              </div>

              {/* Images */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-gray-700">Imagens</label>
                  <button
                    type="button"
                    onClick={addImageField}
                    className="bg-[#1B1F5E] text-white px-3 py-1.5 rounded-lg text-sm"
                  >
                    + Adicionar Imagem
                  </button>
                </div>

                <div className="space-y-4">
                  {editing.images?.map((img, index) => (
                    <div key={index} className="border rounded-xl p-4">
                      {/* URL input + remove */}
                      <div className="flex gap-2 mb-3">
                        <input
                          value={img}
                          onChange={(e) => updateImage(index, e.target.value)}
                          placeholder="URL da imagem"
                          className="flex-1 border p-3 rounded-lg text-sm min-w-0"
                        />
                        <button
                          type="button"
                          onClick={() => removeImageField(index)}
                          className="bg-red-500 text-white px-3 py-2 rounded-lg flex-shrink-0 text-sm font-bold"
                        >
                          ✕
                        </button>
                      </div>

                      {/* Preview */}
                      {img && (
                        <div className="space-y-3">
                          <img
                            src={img}
                            alt={`Imagem ${index + 1}`}
                            className="w-full h-48 object-cover rounded-lg border"
                          />
                          <button
                            type="button"
                            onClick={() => setEditing({ ...editing, main_image: img })}
                            className={`w-full sm:w-auto px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                              editing.main_image === img
                                ? "bg-green-600 text-white"
                                : "border border-gray-300 text-gray-700 hover:bg-gray-50"
                            }`}
                          >
                            {editing.main_image === img ? "✓ Imagem Principal" : "Definir como Principal"}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Video */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL do vídeo
                </label>
                <input
                  value={editing.video_url ?? ""}
                  onChange={(e) => setEditing({ ...editing, video_url: e.target.value })}
                  placeholder="YouTube, Vimeo ou Google Drive"
                  className="w-full border p-3 rounded-lg"
                />
              </div>

              {/* Toggles */}
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editing.visible ?? true}
                    onChange={(e) => setEditing({ ...editing, visible: e.target.checked })}
                    className="w-4 h-4 accent-[#1B1F5E]"
                  />
                  <span className="text-sm text-gray-700">Visível no site</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editing.featured ?? false}
                    onChange={(e) => setEditing({ ...editing, featured: e.target.checked })}
                    className="w-4 h-4 accent-[#1B1F5E]"
                  />
                  <span className="text-sm text-gray-700">Destacado</span>
                </label>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex flex-col-reverse sm:flex-row gap-3 p-5 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={closeModal}
                className="w-full sm:w-auto flex-1 border border-gray-300 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-100 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                className="w-full sm:w-auto flex-1 bg-[#1B1F5E] text-white py-3 px-6 rounded-lg font-medium hover:bg-[#14184a] transition-colors"
              >
                Salvar Produto
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-xl w-full max-w-sm shadow-2xl">
            <h3 className="font-bold text-lg text-gray-800 mb-2">Excluir produto</h3>
            <p className="text-gray-500 mb-6">
              Tem certeza que deseja excluir este produto? Esta ação não pode ser desfeita.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteId(null)}
                className="flex-1 border border-gray-300 text-gray-700 px-4 py-2.5 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(deleteId)}
                className="flex-1 bg-red-500 text-white px-4 py-2.5 rounded-lg font-medium hover:bg-red-600 transition-colors"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
